import type { UploadFile } from 'element-plus'

// Upload image constraints
const IMAGE_ACCEPTABLE_MIME_TYPES = ['image/jpeg', 'image/png']
const PDF_ACCEPTABLE_MIME_TYPES = ['application/pdf']
const IMAGE_MAX_SIZE_BYTES = 10_485_760 // 10MB

/**
 * Validates an upload image's MIME type.
 *
 * @param rule the form rule.
 * @param value the form field value, either a File or an UploadFile
 * @param callback Element Plus form validation callback.
 */
export const uploadMimeTypeValidator =
  (mimeTypes: string[]) => (rule: any, value: File | UploadFile | undefined, callback: any) => {
    if (!value) return callback()

    let isValidType = false
    // Raw file
    if ('type' in value && mimeTypes.includes(value.type)) {
      isValidType = true
    }
    // EP's UploadFile
    if ('raw' in value && value.raw && mimeTypes.includes(value.raw.type)) {
      isValidType = true
    }
    if (value && !isValidType) {
      callback(new Error('File must be a .png or .jpg image.'))
    } else {
      callback()
    }
  }

export const uploadImageMimeTypeValidator = uploadMimeTypeValidator(IMAGE_ACCEPTABLE_MIME_TYPES)
export const uploadImageOrPdfMimeTypeValidator = uploadMimeTypeValidator([
  ...PDF_ACCEPTABLE_MIME_TYPES,
  ...IMAGE_ACCEPTABLE_MIME_TYPES,
])

/**
 * Validates an upload image's size.
 *
 * @param rule the form rule.
 * @param value the form field value.
 * @param callback Element Plus form validation callback.
 */
export const uploadImageSizeValidator = (rule: any, value: File, callback: any) => {
  if (value?.size > IMAGE_MAX_SIZE_BYTES) {
    callback(new Error('File must be less than or equal to 10MB.'))
  } else {
    callback()
  }
}

/**
 * Inbound or outbound tracking information custom validator.
 *
 * @param rule the form rule ('tracking_information').
 * @param value the form tracking information.
 * @param callback async-validator validation callback.
 */
export const validateTrackingInformationGroup = (rule: any, value: any, callback: any) => {
  const hasAtLeastOneTrackingItem = value.tracking_number || value.bol_number || value.waybill_number
  if (!value.carrier || (value.carrier && hasAtLeastOneTrackingItem)) {
    callback()
  } else {
    callback(new Error('At least one tracking number is needed if carrier is set'))
  }
}

/**
 * Validates a number to be greater than zero
 * @param rule the form rule.
 * @param value the form field value.
 * @param callback Element Plus form validation callback.
 */
export const validateIsPositive = (rule: any, value: number, callback: any) => {
  if (value > 0) callback()
  else callback(new Error('Please enter a positive number.'))
}
